import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { RequestNextApi } from "../../../../api/Request";
import { getCookie } from "../../../../utils/cookieHandle";
import { ShowNotification } from "../../../../utils/notification/snackbarUtils";


const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const openInNewTab = (blob) => {
    const url = URL.createObjectURL(blob);
    const newTab = window.open(url, "_blank");
    if (newTab) {
        newTab.onload = () => URL.revokeObjectURL(url);
    } else {
        console.error("Failed to open new tab");
    }
};

const quickDateOptions = [
    {
        label: "2 Months Prior",
        value: "2_months_prior",
    },
    {
        label: "1 Month Prior",
        value: "1_month_prior",
    },
    {
        label: "Current Month",
        value: "current_month",
    },
];

const Wrapper = ({ sku, children }) => {
    if (sku) {
        return children
    }
    return (
        <div className="border-[1px] border-solid border-primary-100 rounded-xl p-[20px]">
            <Typography variant="h4" className="font-normal">
                SKU Report
            </Typography>
            {children}
        </div>

    )
}

function SKUReport({ sku = null }) {
    const [startDate, setStartDate] = React.useState(
        dayjs().subtract(1, "month").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = React.useState(dayjs().format("YYYY-MM-DD"));
    const [loading, setLoading] = React.useState(false);
    const [quickDateSelect, setQuickDateSelect] = React.useState("");
    const [skuInput, setSkuInput] = React.useState("");

    const handleQuickDateSelect = (event) => {
        const value = event.target.value;
        setQuickDateSelect(value);

        const today = dayjs();
        let start, end;

        switch (value) {
            case "2_months_prior":
                start = today.subtract(2, "month").startOf("month").format("YYYY-MM-DD");
                end = today.subtract(2, "month").endOf("month").format("YYYY-MM-DD");
                break;
            case "1_month_prior":
                start = today.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                end = today.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
                break;
            case "current_month":
                start = today.startOf("month").format("YYYY-MM-DD");
                end = today.format("YYYY-MM-DD");
                break;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
    };

    const onRunReport = async (type) => {
        try {
            setLoading(true);

            // Format dates with time components
            const formattedStartDate = `${startDate} 00:00:00`;
            const formattedEndDate = `${endDate} 23:59:59`;

            const result = await RequestNextApi.post('/admin/export/run-export-query',
                {
                    jwtToken: getCookie('jwt-token'),
                    template_id: 13780,
                    response_type: type,
                    filters: {
                        repsku: Number(sku || skuInput),
                        repstart: formattedStartDate,
                        repstop: formattedEndDate
                    }
                },
                {
                    ...(type === "pdf" && { responseType: "blob" }),
                }
            );

            if (
                (type === "html" &&
                    result.data &&
                    typeof result.data === "string") ||
                (type === "json" && result.data.success) || (type === "pdf" && result.data)
            ) {
                if (type === "json") {
                    const jsonString = JSON.stringify(
                        {
                            data: result.data.data
                        },
                        null,
                        2
                    );
                    const blob = new Blob([jsonString], { type: "application/json" });
                    downloadFile(
                        blob,
                        `sku_report_${startDate}_to_${endDate}` + ".json"
                    );
                }
                if (type === "html") {
                    const blob = new Blob([result.data], { type: "text/html" });
                    openInNewTab(blob);
                }
                if (type === "pdf") {
                    const blob = new Blob([result.data], { type: "application/pdf" });
                    openInNewTab(blob)
                }
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (e) {
        } finally {
            setLoading(false);
        }
    };


    return (
        <Wrapper sku={sku}>
            <div className="flex gap-2 mt-4 items-center">
                {!sku && (
                    <TextField
                        color="secondary"
                        focused
                        size="small"
                        type="number"
                        fullWidth
                        className="w-[80%]"
                        value={skuInput}
                        onChange={(e) => setSkuInput(e.target.value)}
                        placeholder="SKU"
                    />
                )}
                <FormControl size="small" fullWidth>
                    <InputLabel id="quick-date-select-label">Quick Date Select</InputLabel>
                    <Select
                        labelId="quick-date-select-label"
                        id="quick-date-select"
                        value={quickDateSelect}
                        label="Quick Date Select"
                        onChange={handleQuickDateSelect}
                    >
                        {quickDateOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Start date"
                    color="secondary"
                    focused
                    size="small"
                    type="date"
                    fullWidth
                    value={startDate}
                    onChange={(e) => {
                        setStartDate(e.target.value);
                        setQuickDateSelect("");
                    }}
                />
                <TextField
                    label="End date"
                    color="secondary"
                    focused
                    size="small"
                    type="date"
                    fullWidth
                    value={endDate}
                    onChange={(e) => {
                        setEndDate(e.target.value);
                        setQuickDateSelect("");
                    }}
                />
                <div className="flex gap-2">
                    <Button
                        variant="contained"
                        className="bg-secondary-500 hover:bg-secondary-500 w-[100px]"
                        onClick={() => {
                            onRunReport("html");
                        }}
                        type="button"
                        disabled={loading || (!sku && !skuInput)}
                    >
                        View
                    </Button>
                    <Button
                        variant="contained"
                        className="bg-secondary-500 hover:bg-secondary-500 w-[100px]"
                        onClick={() => {
                            onRunReport("pdf");
                        }}
                        type="button"
                        disabled={loading || (!sku && !skuInput)}
                    >
                        PDF
                    </Button>
                </div>
            </div>
        </Wrapper>
    );
}

export default SKUReport;
